// Anexa os documentos de um pedido do wizard de Requerimentos à API de Anexo de
// GEDs. Diferente do documentoAnexoService.js do Protocolo Online (que tem
// tipoAssunto/tipoDocumentoProcesso fixos), aqui cada pedido tem seu próprio
// tiposAssunto e cada documento seu próprio tipoDocumentoProcesso, ambos já
// vindos do config.js do pedido (protocoloConfig e documentConfig).
import documentosApi from '../../../../services/documentosApi';
import logger from '../../../../utils/logger';
import { RESUMO_REQUERIMENTO_DOC_ID, RESUMO_REQUERIMENTO_TIPO_DOCUMENTO_PROCESSO } from './resumoRequerimentoConstants';
import { TIPO_DOCUMENTO_ADICIONAL } from '../../../protocolo/services/documentoAnexoService';

// "Anexar mais documentos" (documentConfig.documentosAdicionais, ex: Juntadas):
// cada arquivo extra entra no map de documentos com um id gerado com este
// prefixo e vai sempre como 281 - Documentos Diversos, igual ao Protocolo Online.
export const DOCUMENTO_ADICIONAL_PREFIX = 'documento_adicional_';
export const isDocumentoAdicional = (docId) => String(docId).startsWith(DOCUMENTO_ADICIONAL_PREFIX);

// O Resumo do Requerimento (PDF gerado automaticamente, ver useDocuments.js) não
// vem do documentConfig de nenhum pedido — é injetado em TODOS os pedidos da
// mesma forma, então precisa sempre entrar na lista de resolução aqui, senão
// o anexo falharia com "não tem tipoDocumentoProcesso configurado".
const listarTodosDocumentos = (documentConfig) => [
  ...(documentConfig?.required || []),
  ...(documentConfig?.conditional || []),
  ...(documentConfig?.optional || []),
  { id: RESUMO_REQUERIMENTO_DOC_ID, tipoDocumentoProcesso: RESUMO_REQUERIMENTO_TIPO_DOCUMENTO_PROCESSO }
];

/**
 * @param {Object} params
 * @param {string|number} params.requerimentoId
 * @param {string|number} [params.protocoloId] - só existe pra pedidos sem taxa, já
 *   finalizados (é o ID da linha em DocumentoProtocolo, não o Protocolo.ID)
 * @param {string|number} params.usuarioId
 * @param {string} params.tiposAssunto - vem do protocoloConfig do pedido já resolvido
 * @param {Object} params.documentConfig - config.documentConfig do pedido
 * @param {Object} params.documentos - map { [docId]: File } dos arquivos selecionados
 */
export async function anexarDocumentosRequerimento({ requerimentoId, protocoloId, usuarioId, tiposAssunto, documentConfig, documentos }) {
  if (!requerimentoId) {
    throw new Error('requerimentoId é obrigatório');
  }
  if (!usuarioId) {
    throw new Error('usuarioId é obrigatório');
  }

  const todosDocumentos = listarTodosDocumentos(documentConfig);
  const arquivos = [];
  const tipos = [];

  Object.entries(documentos || {}).forEach(([docId, file]) => {
    if (!file) return;

    const tipoDocumentoProcesso = isDocumentoAdicional(docId)
      ? TIPO_DOCUMENTO_ADICIONAL
      : todosDocumentos.find((doc) => doc.id === docId)?.tipoDocumentoProcesso;
    if (!tipoDocumentoProcesso) {
      throw new Error(`Documento "${docId}" não tem tipoDocumentoProcesso configurado`);
    }

    arquivos.push(file);
    tipos.push(tipoDocumentoProcesso);
  });

  if (arquivos.length === 0) {
    return null;
  }

  const formData = new FormData();
  formData.append('RequerimentoId', requerimentoId);
  if (protocoloId) {
    formData.append('protocoloId', protocoloId);
  }
  formData.append('tipoAssunto', tiposAssunto);
  formData.append('usuarioId', usuarioId);
  formData.append('tipoDocumentoProcesso', tipos.join(','));
  arquivos.forEach((arquivo) => formData.append('arquivos', arquivo));

  try {
    const response = await documentosApi.post('/documentos/anexar', formData);
    return response.data;
  } catch (error) {
    logger.error('Erro ao anexar documentos do requerimento:', error);
    const mensagem = error.response?.data?.error || error.response?.data?.message || error.message;
    throw new Error(mensagem || 'Erro ao anexar documentos do requerimento');
  }
}
